import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/6221bdec-a1f4-4f2a-b3c7-5775bc29aacc/images/e31134d582097747674673484304c177d3bc9e13151123a0f77086a44dfae7a6.png";

export default function RegisterPage() {
  const { register, error, setError } = useAuth();
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const canSubmit = acceptPrivacy && acceptTerms && !busy;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    const ok = await register(name, email, password, { privacy: acceptPrivacy, terms: acceptTerms });
    setBusy(false);
    if (ok) navigate("/diary");
  };

  return (
    <main data-testid="register-page" className="min-h-[calc(100vh-90px)] grid grid-cols-1 lg:grid-cols-5">
      <div className="hidden lg:flex lg:col-span-2 relative border-r border-[#C1B49A] overflow-hidden">
        <div className="absolute inset-0 opacity-70" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "multiply" }} />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <p className="label-small text-[#8B3A3A] mb-4">{t("register.quote_label")}</p>
          <p className="font-display text-3xl italic text-[#1A1918] leading-snug">{t("register.quote")}</p>
        </div>
      </div>

      <div className="lg:col-span-3 flex items-center justify-center p-8 md:p-16">
        <form onSubmit={handleSubmit} className="w-full max-w-md anim-fade-up" data-testid="register-form">
          <p className="label-small mb-3">{t("register.eyebrow")}</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[#1A1918] mb-2">{t("register.heading")}</h1>
          <p className="text-[#4A4238] mb-10">
            {t("register.sub_a")} <span className="italic text-[#8B3A3A]">{t("register.sub_b")}</span>{t("register.sub_c")}
          </p>

          <div className="space-y-7">
            <div>
              <label className="label-small block mb-2">{t("register.label_name")}</label>
              <input data-testid="register-name-input" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="input-etched" autoComplete="name" />
            </div>
            <div>
              <label className="label-small block mb-2">{t("register.label_email")}</label>
              <input data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-etched" autoComplete="email" />
            </div>
            <div>
              <label className="label-small block mb-2">{t("register.label_password")}</label>
              <input data-testid="register-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input-etched" autoComplete="new-password" />
            </div>
            {error && (<p data-testid="register-error" className="text-sm text-[#8B3A3A] font-medium">{error}</p>)}

            <div className="space-y-3 pt-2 border-t border-[#C1B49A]">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  data-testid="register-accept-privacy"
                  type="checkbox"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  className="mt-1 accent-[#8B3A3A] w-4 h-4 shrink-0"
                />
                <span className="text-sm text-[#4A4238] leading-relaxed">
                  {t("register.consent_privacy_a")}{" "}
                  <Link to="/privacy" target="_blank" className="text-[#8B3A3A] underline underline-offset-4 hover:text-[#6B2A2A]" data-testid="register-privacy-link">
                    {t("register.consent_privacy_link")}
                  </Link>
                  {t("register.consent_privacy_b")}
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  data-testid="register-accept-terms"
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 accent-[#8B3A3A] w-4 h-4 shrink-0"
                />
                <span className="text-sm text-[#4A4238] leading-relaxed">
                  {t("register.consent_terms_a")}{" "}
                  <Link to="/terms" target="_blank" className="text-[#8B3A3A] underline underline-offset-4 hover:text-[#6B2A2A]" data-testid="register-terms-link">
                    {t("register.consent_terms_link")}
                  </Link>
                  {t("register.consent_terms_b")}
                </span>
              </label>
            </div>

            <button data-testid="register-submit-btn" type="submit" disabled={!canSubmit} className="btn-primary w-full">
              {busy ? t("register.submitting") : t("register.submit")}
            </button>
            <p className="text-sm text-[#4A4238]">
              {t("register.have_account")}{" "}
              <Link to="/login" className="text-[#8B3A3A] underline underline-offset-4" data-testid="register-to-login-link">{t("register.login_link")}</Link>
              .
            </p>
          </div>
        </form>
      </div>
    </main>
  );
}
